import { useState } from 'react';
import {
  Box,
  ButtonBase,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import PersonSearchRounded from '@mui/icons-material/PersonSearchRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import ContactPhoneRounded from '@mui/icons-material/ContactPhoneRounded';
import { useNavigate } from 'react-router';
import { useAuth } from '@/auth';
import { useNotify } from '@/components/feedback/notificationsContext';
import { UserAvatar } from '@/components/UserAvatar';
import { useEu } from '@/features/notas/EuContext';
import { VerComoAlunoDialog } from '@/features/notas/VerComoAluno';
import { useOfertas } from '@/features/notas/Ofertas';
import { Switch } from '@mui/material';

export function AccountMenu() {
  const { user, signOut } = useAuth();
  const notify = useNotify();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [verComo, setVerComo] = useState(false);
  const { eu } = useEu();
  const navigate = useNavigate();
  const ofertas = useOfertas();

  const handleSignOut = async () => {
    setAnchor(null);
    await signOut();
    notify('Você saiu da sua conta.');
  };

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label="Abrir menu da conta"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        sx={{ borderRadius: '50%', ml: 0.5 }}
      >
        <UserAvatar user={user} sx={{ width: 36, height: 36 }} />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        <Stack direction="row" spacing={1.5} sx={{ px: 1.5, py: 1.25, alignItems: 'center' }}>
          <UserAvatar user={user} sx={{ width: 40, height: 40 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {user?.name ?? 'Usuário'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {user?.email}
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ my: 0.5 }} />
        {eu.admin && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setVerComo(true);
            }}
          >
            <ListItemIcon>
              <PersonSearchRounded fontSize="small" />
            </ListItemIcon>
            Ver como aluno…
          </MenuItem>
        )}
        {eu.admin && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              void navigate('/admin?aba=publicacao');
            }}
          >
            <ListItemIcon>
              <FactCheckRounded fontSize="small" />
            </ListItemIcon>
            Aprovar publicações
          </MenuItem>
        )}
        {ofertas.disponivel && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              void navigate('/');
              window.setTimeout(
                () =>
                  document
                    .getElementById('seus-contatos')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
                400,
              );
            }}
          >
            <ListItemIcon>
              <ContactPhoneRounded fontSize="small" />
            </ListItemIcon>
            Meus contatos (celular e ofertas)
          </MenuItem>
        )}
        {ofertas.disponivel && (
          <MenuItem onClick={() => void ofertas.alterna()} disabled={ofertas.salvando}>
            <Box sx={{ flex: 1, pr: 1 }}>
              <Typography variant="body2">Receber novidades do professor</Typography>
              <Typography variant="caption" color="text.secondary">
                {ofertas.ligado ? 'ligado' : 'desligado'} · um clique muda
              </Typography>
            </Box>
            <Switch
              size="small"
              checked={ofertas.ligado}
              tabIndex={-1}
              readOnly
              sx={{ pointerEvents: 'none' }}
            />
          </MenuItem>
        )}
        <MenuItem onClick={() => void handleSignOut()}>
          <ListItemIcon>
            <LogoutRounded fontSize="small" />
          </ListItemIcon>
          Sair
        </MenuItem>
      </Menu>
      <VerComoAlunoDialog aberto={verComo} onFechar={() => setVerComo(false)} />
    </>
  );
}
